import type { ColumnDef } from '@deckspace/types';

/**
 * Local mapping shape for the Table visualization. Lives here (not in
 * `@deckspace/types`) because DataTable is its own ComponentNode type — dispatch
 * happens via the node `type` field, not via a `kind` discriminator on mapping.
 */
export interface TableMapping {
  visibleColumns?: string[];
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
}

/**
 * Result shape consumed by the Table visualization component.
 * Columns are the visible column names in display order; rows are the
 * corresponding cell values in the same order.
 */
export interface TableResult {
  columns: string[];
  rows: (string | number)[][];
}

/**
 * Project row objects into a visible-columns matrix, optionally sorted.
 *
 * - `visibleColumns` defaults to every column in `columnDefs` order.
 * - Sort is stable-ish: nulls/empty strings sort last regardless of direction.
 * - Null/undefined cells are coerced to the empty string for display parity
 *   with the legacy Table component.
 */
export function prepTableSeries(
  rows: Record<string, unknown>[],
  columnDefs: ColumnDef[],
  mapping: TableMapping
): TableResult {
  const allCols = columnDefs.map((c) => c.name);
  const visible = mapping.visibleColumns?.length
    ? mapping.visibleColumns.filter((c) => allCols.includes(c))
    : allCols;

  let mapped: (string | number)[][] = rows.map((row) =>
    visible.map((c) => {
      const v = row[c];
      return (v === null || v === undefined ? '' : v) as string | number;
    })
  );

  if (mapping.sortColumn && visible.includes(mapping.sortColumn)) {
    const idx = visible.indexOf(mapping.sortColumn);
    const dir = mapping.sortDirection === 'desc' ? -1 : 1;
    mapped = mapped.slice().sort((a, b) => {
      const av = a[idx];
      const bv = b[idx];
      // Empty/missing always sort to end
      const aEmpty = av === undefined || av === '';
      const bEmpty = bv === undefined || bv === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      if (av === bv) return 0;
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }

  return { columns: visible, rows: mapped };
}
